import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { UserCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { canManageRoles } from '../../constants/adminRoles';
import { getAdminUser } from '../../services/admin/usersService';
import {
  ADMIN_DATE_PLACEHOLDER,
  adminRoleLabel,
  classifyAdminCallableError,
  formatAdminAddress,
  formatAdminDate,
  visibleAdminProfileFields,
} from './adminUserDisplay';
import AdminChangeRoleDialog from './AdminChangeRoleDialog';
import {
  buildImpersonationReturnPath,
  reloadToAccountHome,
  setImpersonationReturnPath,
} from './impersonationNav';

function personaLabel(t, persona) {
  if (!persona) return ADMIN_DATE_PLACEHOLDER;
  const key = `persona.${persona}`;
  const label = t(key);
  return label === key ? persona : label;
}

function approvalLabel(t, status) {
  if (!status) return ADMIN_DATE_PLACEHOLDER;
  const key = `admin.users.approval.${status}`;
  const label = t(key);
  return label === key ? status : label;
}

function Field({ label, children }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-slate-900">{children}</dd>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4">
      <h2 className="text-sm font-semibold text-slate-800">{title}</h2>
      <dl className="mt-3 grid gap-3 sm:grid-cols-2">{children}</dl>
    </section>
  );
}

export default function AdminUserDetailPage() {
  const { uid } = useParams();
  const location = useLocation();
  const { t, language } = useLanguage();
  const { startImpersonation, adminRole } = useAuth();
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState('loading');
  const [errorKind, setErrorKind] = useState(null);
  const [impersonateBusy, setImpersonateBusy] = useState(false);
  const [impersonateError, setImpersonateError] = useState('');
  const [roleTarget, setRoleTarget] = useState(null);
  const [roleSavedMessage, setRoleSavedMessage] = useState('');
  const role = adminRole || 'super_admin';
  const showRoleActions = canManageRoles(role);

  const load = useCallback(async () => {
    const targetUid = String(uid || '').trim();
    if (!targetUid) {
      setUser(null);
      setStatus('not-found');
      return;
    }
    setStatus('loading');
    setErrorKind(null);
    setImpersonateError('');
    try {
      const data = await getAdminUser(targetUid);
      setUser(data);
      setStatus('ready');
    } catch (err) {
      setUser(null);
      const kind = classifyAdminCallableError(err);
      if (kind === 'not-found') {
        setStatus('not-found');
      } else {
        setErrorKind(kind);
        setStatus('error');
      }
    }
  }, [uid]);

  useEffect(() => {
    load();
  }, [load]);

  const handleImpersonate = async () => {
    if (!user || user.canImpersonate !== true || impersonateBusy) return;
    setImpersonateError('');
    setImpersonateBusy(true);
    try {
      await startImpersonation({ targetUid: user.uid });
      setImpersonationReturnPath(buildImpersonationReturnPath(location));
      reloadToAccountHome();
    } catch (err) {
      const kind = classifyAdminCallableError(err);
      const message =
        kind === 'not-found'
          ? t('admin.users.impersonate.error.notFound')
          : kind === 'permission-denied'
            ? t('admin.users.impersonate.error.denied')
            : t('admin.users.impersonate.error');
      setImpersonateError(message);
      setImpersonateBusy(false);
    }
  };

  const errorMessage =
    errorKind === 'unavailable'
      ? t('admin.users.error.unavailable')
      : errorKind === 'internal'
        ? t('admin.users.error.internal')
        : t('admin.users.detail.error');

  const profileFields = visibleAdminProfileFields(user?.profile);
  const address = formatAdminAddress(user?.profile);
  const propertyItems = Array.isArray(user?.properties?.items) ? user.properties.items : [];
  const totalCount = Number(user?.properties?.totalCount) || 0;

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{t('admin.users.detail.title')}</h1>
          {user?.displayName ? (
            <p className="mt-1 text-sm text-slate-600">{user.displayName}</p>
          ) : null}
        </div>
        <Link
          to="/admin/users"
          className="text-sm font-semibold text-indigo-600 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {t('admin.users.detail.back')}
        </Link>
      </div>

      {status === 'loading' ? (
        <p className="text-sm text-slate-500" aria-live="polite">
          {t('admin.users.detail.loading')}
        </p>
      ) : null}

      {status === 'not-found' ? (
        <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
          {t('admin.users.detail.notFound')}
        </p>
      ) : null}

      {status === 'error' ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p>{errorMessage}</p>
          <button
            type="button"
            onClick={load}
            className="mt-2 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-semibold text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            {t('admin.users.retry')}
          </button>
        </div>
      ) : null}

      {status === 'ready' && user ? (
        <>
          {user.canImpersonate === true ? (
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
              <button
                type="button"
                onClick={handleImpersonate}
                disabled={impersonateBusy}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
              >
                <UserCheck className="h-4 w-4" aria-hidden="true" />
                {impersonateBusy ? t('admin.users.impersonate.busy') : t('admin.users.impersonate')}
              </button>
              {impersonateError ? (
                <p className="mt-2 text-sm text-red-700">{impersonateError}</p>
              ) : null}
            </div>
          ) : null}

          {roleSavedMessage ? (
            <p className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" role="status">
              {roleSavedMessage}
            </p>
          ) : null}

          {showRoleActions ? (
            <section className="rounded-xl border border-slate-200 bg-white p-4">
              <h2 className="text-sm font-semibold text-slate-800">{t('admin.users.section.administration')}</h2>
              <dl className="mt-3 grid gap-3 sm:grid-cols-2">
                <Field label={t('admin.users.changeRole.current')}>
                  {adminRoleLabel(user.rentalyzerRole, t)}
                </Field>
              </dl>
              <button
                type="button"
                onClick={() => setRoleTarget(user)}
                className="mt-3 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {t('admin.users.changeRole')}
              </button>
            </section>
          ) : null}

          <Section title={t('admin.users.section.identity')}>
            <Field label={t('admin.users.field.displayName')}>
              {user.displayName || ADMIN_DATE_PLACEHOLDER}
            </Field>
            <Field label={t('admin.users.field.email')}>{user.email || ADMIN_DATE_PLACEHOLDER}</Field>
            <Field label={t('admin.users.field.uid')}>
              <code className="break-all text-xs">{user.uid}</code>
            </Field>
            <Field label={t('admin.users.field.role')}>{adminRoleLabel(user.rentalyzerRole, t)}</Field>
            <Field label={t('admin.users.field.enabled')}>
              {user.disabled ? t('admin.users.status.disabled') : t('admin.users.status.enabled')}
            </Field>
          </Section>

          <Section title={t('admin.users.section.account')}>
            <Field label={t('admin.users.field.creationTime')}>
              {formatAdminDate(user.creationTime, language)}
            </Field>
            <Field label={t('admin.users.field.lastSignInTime')}>
              {formatAdminDate(user.lastSignInTime, language)}
            </Field>
            <Field label={t('admin.users.field.persona')}>{personaLabel(t, user.persona)}</Field>
            <Field label={t('admin.users.field.personaApprovalStatus')}>
              {approvalLabel(t, user.personaApprovalStatus)}
            </Field>
          </Section>

          {profileFields.length > 0 || address ? (
            <Section title={t('admin.users.section.profile')}>
              {profileFields.map((row) => {
                let display = row.value;
                if (row.kind === 'boolean') display = row.value ? t('admin.users.yes') : t('admin.users.no');
                else if (row.kind === 'date') display = formatAdminDate(row.value, language);
                else if (row.kind === 'persona') display = personaLabel(t, row.value);
                return (
                  <Field key={row.key} label={t(`admin.users.field.${row.key}`)}>
                    {String(display)}
                  </Field>
                );
              })}
              {address ? (
                <Field label={t('admin.users.field.address')}>{address}</Field>
              ) : null}
            </Section>
          ) : null}

          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <h2 className="text-sm font-semibold text-slate-800">{t('admin.users.section.properties')}</h2>
            <p className="mt-2 text-sm text-slate-600">
              {t('admin.users.properties.total').replace('{count}', String(totalCount))}
            </p>
            {totalCount > propertyItems.length ? (
              <p className="mt-1 text-sm text-slate-500">
                {t('admin.users.properties.shown')
                  .replace('{shown}', String(propertyItems.length))
                  .replace('{total}', String(totalCount))}
              </p>
            ) : null}
            {propertyItems.length === 0 ? (
              <p className="mt-3 text-sm text-slate-500">{t('admin.users.properties.empty')}</p>
            ) : (
              <ul className="mt-3 divide-y divide-slate-100">
                {propertyItems.map((item) => (
                  <li key={item.id} className="py-2 text-sm">
                    <p className="font-medium text-slate-900">
                      {item.address || ADMIN_DATE_PLACEHOLDER}
                    </p>
                    <p className="break-all text-xs text-slate-500">
                      {t('admin.users.properties.id')}: {item.id}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      ) : null}

      <AdminChangeRoleDialog
        open={Boolean(roleTarget)}
        user={roleTarget}
        t={t}
        onClose={() => setRoleTarget(null)}
        onSaved={() => {
          setRoleTarget(null);
          setRoleSavedMessage(t('admin.users.changeRole.success'));
          load();
        }}
      />
    </div>
  );
}
